import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { TextLink } from "components/actions/Links";
import { ExportNoticesButton } from "components/notices/ExportNoticesButton";
import { NewNoticeTable } from "components/notices/NewNoticeTable";
import {
  convertNoticeFiltersToSearchRequest,
  EMPTY_FILTERS,
  NoticeFilters,
} from "components/notices/utils";
import Modal from "components/ui/modal/Modal";
import Tabs, { TabItem } from "components/ui/tabs/Tabs";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import { ColumnSettingFieldEnum } from "lib/generated/app-api";
import { useNotices } from "lib/hooks/api/notices/useNotices";
import { routes } from "lib/routes";
import { SortState } from "lib/search/types";
import { SupplierMentionType } from "lib/types/graphQLEnums";
import { Logo } from "../saved/utils";
import { OrgDetails } from "./SupplierRelationshipsCharts";

const TABLE_COLUMNS: ColumnSettingFieldEnum[] = [
  "title",
  "stage",
  "value",
  "published_date",
  "expiry_date",
  "qualification",
];

const TabKey = {
  allNotices: "allNotices",
  similarNotices: "similarNotices",
};

type NoticesModalProps = {
  isOpen: boolean;
  supplier: OrgDetails;
  buyer: OrgDetails;
  similarNoticesIds: string[];
  onOpenChange: (open: boolean) => void;
};

function SupplierBuyerNoticesModal({
  isOpen,
  onOpenChange,
  supplier,
  buyer,
  similarNoticesIds,
}: NoticesModalProps) {
  const tableFilters = {
    ...EMPTY_FILTERS,
    supplierMentionType: SupplierMentionType.DirectlyAwarded,
    buyers: [buyer.guid],
    supplierGuids: [supplier.guid],
  };

  const tabItems: TabItem[] = [
    {
      key: TabKey.allNotices,
      label: "All notices",
      children: <NoticesTabContent tableFilters={tableFilters} />,
    },
    {
      key: TabKey.similarNotices,
      label: "Similar notices",
      children: (
        <NoticesTabContent tableFilters={{ ...tableFilters, noticeIds: similarNoticesIds }} />
      ),
    },
  ];

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange}>
      <Flex column gap={16}>
        <ModalHeader h3 as="div">
          <Flex>
            <span>Notices between </span>
            <OrganisationLink
              name={buyer.name}
              profileUrl={`${routes.buyer}/${buyer.guid}`}
              logoUrl={buyer.logoUrl}
            />
            <span> and </span>
            <OrganisationLink
              name={supplier.name}
              profileUrl={`${routes.supplier}/${supplier.guid}`}
              logoUrl={supplier.logoUrl}
            />
          </Flex>
        </ModalHeader>
        <Tabs items={tabItems} defaultValue={TabKey.allNotices} />
      </Flex>
    </Modal>
  );
}

function NoticesTabContent({ tableFilters }: { tableFilters: NoticeFilters }) {
  const [filters, setFilters] = useState<NoticeFilters>(tableFilters);
  const [selectedNotices, setSelectedNotices] = useState<string[]>();

  const { data } = useNotices(
    convertNoticeFiltersToSearchRequest(filters, { current: 1, pageSize: 10 }),
    false,
  );

  const onSortChange = (sortState: SortState) => {
    setFilters({ ...filters, sort: sortState });
  };

  useEffect(() => {
    setFilters(tableFilters);
  }, [tableFilters]);

  return (
    <TabContent>
      <Flex column gap={16}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text>{data?.pagingInfo?.totalResults ?? 0} notices</Text>
          <Flex gap={8}>
            {selectedNotices && selectedNotices.length > 0 && (
              <ClearSelectedButton
                count={selectedNotices.length}
                onClear={() => setSelectedNotices([])}
              />
            )}
            <ExportNoticesButton filters={filters} selectedNotices={selectedNotices} />
          </Flex>
        </Flex>
        <NewNoticeTable
          columnSettings={TABLE_COLUMNS}
          filters={filters}
          onSortChange={(sortState: SortState) => onSortChange(sortState)}
          selectedRows={selectedNotices}
          onSelectedRowsChange={(rows) => setSelectedNotices(rows)}
        />
      </Flex>
    </TabContent>
  );
}

function OrganisationLink({
  name,
  profileUrl,
  logoUrl,
}: {
  name: string;
  profileUrl: string;
  logoUrl?: string | null;
}) {
  return (
    <Flex alignItems="center" gap={6}>
      <Logo url={logoUrl} size={16} />
      <TextLink to={profileUrl}>{name}</TextLink>
    </Flex>
  );
}

const ModalHeader = styled(Text)(({ theme }) => ({
  margin: "-16px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  padding: 24,
}));

const TabContent = styled(Flex)({
  paddingTop: 16,
});

export default SupplierBuyerNoticesModal;
